import React, { useState, useMemo } from 'react';
import { Storage, Translate } from 'react-jhipster';
import { Dropdown, DropdownItem, DropdownMenu, Nav, Navbar } from 'reactstrap';
import { isRTL } from 'app/config/translation';
import { LocaleMenu, EntitiesMenu } from '../menus';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { setLocale } from 'app/shared/reducers/locale';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import SettingsIcon from '@mui/icons-material/Settings';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { Avatar, Badge } from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import HomeIcon from '@mui/icons-material/Home';
import Box from '@mui/material/Box';
import { AUTHORITIES, USER_TYPE } from 'app/config/constants';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useHistory, useLocation } from 'react-router-dom';
import { updateUserLanguage, updateUserLanguageForAllRoles } from 'app/modules/administration/user-management/user-management.reducer';
import EzraChatWidget from './ezra-chat-widget';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import moment from 'moment';
import { updateEntity as updateNotification, dismissedAllNotification } from 'app/entities/notification-record/notification-record.reducer';
import { getEntity as getQuotationEntity } from 'app/entities/quotation/quotation.reducer';
import NotificationsAllDialog from './notifications-all-dialog';

export interface IHeaderNavBarProps {
  isAuthenticated: boolean;
  currentLocale: string;
  account: any;
  notification: any[];
  currentNotification: any[];
}

const HeaderNavBar = (props: IHeaderNavBarProps) => {
  const { isAuthenticated, currentLocale, account, notification, currentNotification } = props;

  const history = useHistory();
  const location = useLocation();
  const isBoubyanQuotationRoute = location.pathname.includes('/quotation/boubyan/');
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.userManagement.user);

  const [menuClicked, setMenuClicked] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<HTMLElement | null>(null);
  const [notificationsDialogOpen, setNotificationsDialogOpen] = useState(false);

  const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
      right: 0,
      top: 8,
      border: `2px solid ${theme.palette.background.paper}`,
      padding: '0 4px',
      textAlign: 'left',
    },
  }));

const isBulkEnabled = useMemo(() => account?.isBulkTransactionEnabled ?? false, [account]);

  const totalNotificationFunction = () => {
    let numberOfNotification = 0;
    notification &&
      notification?.filter(n => {
        if (n?.notificationTemplate?.type !== 'timeOutBursa' && !n.dismissed) {
          numberOfNotification++;
        }
      });
    currentNotification &&
      currentNotification?.filter(n => {
        if (n?.notificationTemplate?.type !== 'timeOutBursa' && !n.dismissed) {
          numberOfNotification++;
        }
      });
    return numberOfNotification;
  };

  // Combine and sort notifications: unread first, filter out timeOutBursa
  const allNotifications = useMemo(() => {
    const combined = [
      ...(currentNotification || []),
      ...(notification || []),
    ].filter(n => n?.notificationTemplate?.type !== 'timeOutBursa');
    // unread first
    return [...combined].sort((a, b) => {
      if (a.dismissed === b.dismissed) return 0;
      return a.dismissed ? 1 : -1;
    });
  }, [currentNotification, notification]);

  const handleDismissedAll = () => {
    dispatch(dismissedAllNotification(account?.id));
  };

  const handleOpenOrder = (orderId: string, statusType: string) => {
    if (statusType === 'poApprovalRequested' || statusType === 'poDelayApprovalForAlshmokhUser') {
      history.push(`/quotation/purchase/${orderId}`);
    } else {
      history.push(`/quotation/bought/${orderId}`);
    }
    dispatch(getQuotationEntity(orderId));
    setNotificationAnchorEl(null);
    setNotificationsDialogOpen(false);
  };

  const handleOpenOrderAndDismiss = (orderId: string, statusType: string, entity: any) => {
    if (statusType === 'poApprovalRequested' || statusType === 'poDelayApprovalForAlshmokhUser') {
      history.push(`/quotation/purchase/${orderId}`);
    } else {
      history.push(`/quotation/bought/${orderId}`);
    }
    dispatch(getQuotationEntity(orderId));
    dispatch(updateNotification({ ...entity, dismissed: true }));
    setNotificationAnchorEl(null);
    setNotificationsDialogOpen(false);
  };

  const handleLocaleChange = event => {
    const langKey = event.target.value;
    document.querySelector('html').setAttribute('dir', isRTL(langKey) ? 'rtl' : 'ltr');
    Storage.session.set('locale', langKey);
    Storage.local.set('locale', langKey);
    dispatch(setLocale(langKey));
    try {
      const updatedUser = {
        ...user,
        id: account?.id,
        langKey: event.target.value,
      };
      if (account?.authorities.includes(AUTHORITIES.ADMIN)) {
        dispatch(updateUserLanguage(updatedUser));
      } else {
        dispatch(updateUserLanguageForAllRoles(updatedUser));
      }
    } catch (TypeError) {
      console.warn('Account not defined yet');
    }
  };

  const handleResponsiveMenu = () => {
    if (!menuClicked) setMenuClicked(true);
    else setMenuClicked(false);
  };

  const toggleAccountMenu = () => setAccountMenuOpen(!accountMenuOpen);

  const toggleUserMenu = () => setUserMenuOpen(!userMenuOpen);

  const handleLogout = () => {
    window.localStorage.removeItem('tab');
    sessionStorage.removeItem('filterBank');
    sessionStorage.removeItem('filterBranch');
    sessionStorage.removeItem('filterCustomer');
    sessionStorage.removeItem('filterCommodity');
    sessionStorage.removeItem('filterDate');
    sessionStorage.removeItem('filterStatus');
    sessionStorage.removeItem('filterSearch');
    sessionStorage.removeItem('filterQuoSearch');
    sessionStorage.removeItem('otpStatus');
    Storage.local.remove('otpStatus');
    Storage.session.remove('otpStatus');
    sessionStorage.removeItem('jhi-authenticationToken');
    Storage.local.remove('jhi-authenticationToken');
    Storage.session.remove('jhi-authenticationToken');
    localStorage.removeItem('RememberMe');
    localStorage.removeItem('userListType');
    localStorage.removeItem('staff');
    localStorage.removeItem('customerPrint');
    localStorage.removeItem('isCustomerPrint');
    localStorage.removeItem('currency');
    localStorage.removeItem('printQuo');
    localStorage.removeItem('status');
    localStorage.removeItem('branch');
    localStorage.removeItem('print');
    localStorage.removeItem('tableView');
    localStorage.removeItem('tableViewFeeReports');
    localStorage.removeItem('commodity');
    localStorage.removeItem('userName');
    localStorage.removeItem('period');
    localStorage.removeItem('bank');
    localStorage.removeItem('totalAmount');
  };

  const settingsLink = () => {
    if (account?.authorities?.includes(AUTHORITIES.ALSHMOKH_USER)) {
      if (account?.authorities?.includes(AUTHORITIES.SUBADMIN_OPERATING_HOURS)) {
        return 'account/stocksettings';
      } else if (account?.authorities?.includes(AUTHORITIES.SUBADMIN_CURRENCIES)) {
        return 'account/currency';
      } else if (account?.authorities?.includes(AUTHORITIES.SUBADMIN_COMMODITY)) {
        return 'account/commodity';
      } else if (account?.authorities?.includes(AUTHORITIES.SUBADMIN_BURSA_FEE)) {
        return 'account/feesettings';
      } else if (account?.authorities?.includes(AUTHORITIES.SUBADMIN_NOTIFY_CANCELLATION)) {
        return 'account/notifycancellation';
      }
    } else {
      return 'account/stocksettings';
    }
  };

  return (
    <>
      <Navbar data-cy="navbar" light expand="lg" fixed="top" className={isBoubyanQuotationRoute ? 'jh-navbar jh-navbar-boubyan' : 'jh-navbar'}>
        <Container>
          <div className="menu-navbar">
            <div className="nav-home">
              <div
                onClick={isBoubyanQuotationRoute ? undefined : () => history.push('/home')}
                className={isBoubyanQuotationRoute ? 'nav-links' : 'nav-links cursor-pointer'}
              >
                <img className="imageLogo" src={isBoubyanQuotationRoute ? "content/images/boubyan-logo.png" : "content/images/img.png"} alt="Logo" />
              </div>
              {!isBoubyanQuotationRoute && (
                <div>
                  <div onClick={() => history.push('/home')} className="nav-links cursor-pointer">
                    <HomeIcon />
                    <div>
                      <Translate contentKey="global.menu.home" />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <Nav id="header-tabs" className="ms-auto">
              <ul className="nav-links-mobile">
                <div className="nav-links locale-icon-mobile">
                  <LocaleMenu currentLocale={currentLocale} onClick={handleLocaleChange} />
                </div>
                <li>
                  <a
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => setNotificationAnchorEl(e.currentTarget as unknown as HTMLElement)}
                    className="nav-links"
                  >
                    <StyledBadge badgeContent={totalNotificationFunction()} max={9} color="error">
                      <NotificationsNoneRoundedIcon />
                    </StyledBadge>
                    <div>
                      <Translate contentKey="global.menu.notifications" />
                    </div>
                  </a>
                </li>
                <li>
                  <div className="menu-icon mx-3" onClick={handleResponsiveMenu}>
                    {menuClicked ? <CloseIcon /> : <MenuIcon />}
                  </div>
                </li>
              </ul>

              <ul className={menuClicked ? 'nav-menu active' : 'nav-menu'}>
                <div className="nav-links" style={menuClicked ? { display: 'none' } : { display: 'flex' }}>
                  <LocaleMenu currentLocale={currentLocale} onClick={handleLocaleChange} />
                </div>
                {(account?.authorities?.includes(AUTHORITIES.ADMIN) ||
                  account?.authorities?.includes(AUTHORITIES.ALSHMOKH_ADMIN) ||
                  account?.authorities?.includes(AUTHORITIES.SUBADMIN_BANK) ||
                  account?.authorities?.includes(AUTHORITIES.SUBADMIN_BRANCH)) && (
                  <li className="cursor-pointer">
                    {' '}
                    <div onClick={() => history.push('/bank')} className="nav-links">
                      <AccountBalanceIcon
                        sx={
                          menuClicked
                            ? { display: 'none', width: 22, height: 21, paddingTop: 0.4, paddingBottom: 0, marginBottom: 0.3 }
                            : { display: 'flex', width: 22, height: 21, paddingTop: 0.4, paddingBottom: 0, marginBottom: 0.3 }
                        }
                      />
                      <div onClick={() => history.push('/bank')}>
                        <Translate contentKey="global.menu.banks" />
                      </div>
                    </div>
                  </li>
                )}
                {(account?.authorities?.includes(AUTHORITIES.ADMIN) || account?.authorities?.includes(AUTHORITIES.ALSHMOKH_ADMIN) ||
                    (account?.authorities?.includes(AUTHORITIES.SUBADMIN_BANK) || account?.authorities?.includes(AUTHORITIES.SUBADMIN_BRANCH)) ||
                    account?.authorities?.includes(AUTHORITIES.SUBADMIN_REBATE_PENDING_CHECKER)) &&
                  <li className='cursor-pointer'> <div onClick={() => history.push('/rebate')} className="nav-links">
                    <ReceiptLongIcon sx={menuClicked ?
                      { display: 'none', width: 22, height: 21, paddingTop: 0.4, paddingBottom: 0, marginBottom: 0.3 } :
                      { display: 'flex', width: 22, height: 21, paddingTop: 0.4, paddingBottom: 0, marginBottom: 0.3 }} />
                    <div onClick={() => history.push('/rebate')}>
                      <Translate contentKey="global.menu.rebate" />
                    </div>
                  </div>
                  </li>}
                {(account?.authorities?.includes(AUTHORITIES.ADMIN) ||
                  account?.authorities?.includes(AUTHORITIES.ALSHMOKH_ADMIN) ||
                  account?.authorities?.includes(AUTHORITIES.SUBADMIN_USER)) && (
                  <li className="cursor-pointer">
                    <div className="nav-links" style={{ marginBottom: '-20px' }}>
                      <Dropdown isOpen={userMenuOpen} toggle={toggleUserMenu}>
                        <span
                          onClick={toggleUserMenu}
                          data-toggle="dropdown"
                          aria-haspopup="true"
                          aria-expanded={userMenuOpen}
                          className="dropdown-account"
                        >
                          <GroupIcon
                            sx={
                              menuClicked
                                ? { display: 'none', width: 28, height: 35, marginBottom: -0.9, marginTop: -0.5, paddingBottom: 0.5 }
                                : {
                                    display: 'flex',
                                    width: 28,
                                    height: 35,
                                    marginBottom: -0.9,
                                    marginTop: -0.5,
                                    paddingBottom: 0.5,
                                    marginRight: Storage.session.get('locale') === 'en' ? 1 : 3,
                                    marginLeft: Storage.session.get('locale') === 'en' ? 1 : 0,
                                  }
                            }
                          />
                          <div>
                            <Translate contentKey="global.menu.users" />
                          </div>
                        </span>
                        <DropdownMenu className="dropdown-user" style={{ width: 'auto', marginTop: '52px' }}>
                          <DropdownItem
                            onClick={() => {
                              localStorage.setItem('userListType', USER_TYPE.STAFF);
                              history.push('/admin/user-management');
                            }}
                          >
                            <Translate contentKey="global.menu.staff" />
                          </DropdownItem>
                          <DropdownItem
                            onClick={() => {
                              localStorage.setItem('userListType', USER_TYPE.CUSTOMER);
                              history.push('/customer');
                            }}
                          >
                            <Translate contentKey="global.menu.customer" />
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    </div>
                  </li>
                )}
                {(account?.authorities?.includes(AUTHORITIES.ADMIN) ||
                  account?.authorities?.includes(AUTHORITIES.ALSHMOKH_ADMIN) ||
                  account?.authorities?.includes(AUTHORITIES.SUBADMIN_BURSA_FEE) ||
                  account?.authorities?.includes(AUTHORITIES.SUBADMIN_COMMODITY) ||
                  account?.authorities?.includes(AUTHORITIES.SUBADMIN_CURRENCIES) ||
                  account?.authorities?.includes(AUTHORITIES.SUBADMIN_NOTIFY_CANCELLATION) ||
                  account?.authorities?.includes(AUTHORITIES.SUBADMIN_OPERATING_HOURS)) && (
                  <li className="cursor-pointer">
                    {' '}
                    <div onClick={() => history.push(`/${settingsLink()}`)} className="nav-links">
                      <SettingsIcon
                        sx={
                          menuClicked
                            ? { display: 'none', width: 23, height: 22, paddingTop: 0.1, marginBottom: 0.3 }
                            : { display: 'flex', width: 23, height: 22, paddingTop: 0.1, marginBottom: 0.3 }
                        }
                      />
                      <div>
                        <Translate contentKey="global.menu.appSetting" />
                      </div>
                    </div>
                  </li>
                )}
                {account?.authorities?.includes(AUTHORITIES.BANK_STAFF) && isBulkEnabled && (
                  <li className="cursor-pointer">
                    {' '}
                    <div onClick={() => history.push('/upload-transaction')} className="nav-links">
                      <DescriptionOutlinedIcon
                        sx={
                          menuClicked
                            ? { display: 'none', width: 23, height: 22, paddingTop: 0.1, marginBottom: 0.3 }
                            : { display: 'flex', width: 23, height: 22, paddingTop: 0.1, marginBottom: 0.3 }
                        }
                      />
                      <div>
                        <Translate contentKey="global.menu.bulkTransaction" />
                      </div>
                    </div>
                  </li>
                )}
                <li style={menuClicked ? { display: 'none' } : { display: 'flex' }}>
                  <a
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => setNotificationAnchorEl(e.currentTarget as unknown as HTMLElement)}
                    className="nav-links"
                  >
                    <StyledBadge badgeContent={totalNotificationFunction()} max={9} color="error">
                      <NotificationsNoneRoundedIcon
                        sx={
                          menuClicked
                            ? { display: 'none', width: 20.5, height: 35, marginBottom: -0.9, marginTop: -0.5, paddingBottom: 0.5 }
                            : { display: 'flex', width: 20.5, height: 35, marginBottom: -0.9, marginTop: -0.5, paddingBottom: 0.5 }
                        }
                      />
                    </StyledBadge>
                    <div>
                      <Translate contentKey="global.menu.notifications" />
                    </div>
                  </a>
                </li>
                <div style={{ marginTop: -10 }}>
                  {isAuthenticated && account?.authorities?.includes(AUTHORITIES.ADMIN) && <EntitiesMenu account={account} />}
                </div>
                <li>
                  <div className="menu-navbar account" style={{ alignItems: 'center' }}>
                    <Dropdown isOpen={accountMenuOpen} toggle={toggleAccountMenu}>
                      <span
                        onClick={toggleAccountMenu}
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded={accountMenuOpen}
                        className="dropdown-account"
                      >
                        Hi, {account?.firstName ? account?.firstName : account?.lastName}
                      </span>
                      <DropdownMenu style={{ width: 'auto', marginTop: '32px' }}>
                        {!isBoubyanQuotationRoute && (
                          <DropdownItem
                            onClick={() =>
                              history.push({
                                pathname: '/admin/user-management/' + account?.login + '/edit',
                                state: { fromMyAccount: true },
                              })
                            }
                          >
                            <Translate contentKey="global.menu.account.myAccount" />
                          </DropdownItem>
                        )}
                        <DropdownItem href={isBoubyanQuotationRoute ? "/boubyan/logout" : "/logout"} onClick={() => handleLogout()}>
                          <Translate contentKey="global.menu.account.logout" />
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                    &nbsp;
                    <Avatar style={{ cursor: 'pointer' }} onClick={toggleAccountMenu}>
                      {account?.firstName?.charAt(0).toUpperCase()}
                      {account?.lastName?.charAt(0).toUpperCase()}
                    </Avatar>
                  </div>
                </li>
              </ul>
            </Nav>
          </div>
        </Container>
      </Navbar>

      {/* Notification Popover — plain white on Boubyan quotation pages instead of the default blue gradient,
          mirroring the old notification drawer's .drawer-content-boubyan override (header.scss). */}
      <Popover
        open={Boolean(notificationAnchorEl)}
        anchorEl={notificationAnchorEl}
        onClose={() => setNotificationAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 380,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            mt: 1,
            background: isBoubyanQuotationRoute ? '#ffffff' : 'linear-gradient(90.72deg, #2476f0 0.32%, #00207c 103.75%)',
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, py: 1.75 }}>
          <Typography fontWeight={700} fontSize={15} sx={{ color: isBoubyanQuotationRoute ? '#212121' : '#fff' }}>
            <Translate contentKey="global.menu.notifications" /> ({totalNotificationFunction()})
          </Typography>
          <Button
            size="small"
            sx={{
              textTransform: 'none',
              color: isBoubyanQuotationRoute ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.85)',
              p: 0,
              fontSize: 13,
              '&:hover': { color: isBoubyanQuotationRoute ? '#212121' : '#fff', background: 'transparent' },
            }}
            onClick={() => {
              handleDismissedAll();
              setNotificationAnchorEl(null);
            }}
          >
            <Translate contentKey="global.rightMenuNotification.markAll" />
          </Button>
        </Box>

        {/* Notification items - show first 3 */}
        <Box sx={{ px: 1.5, pb: 0.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {allNotifications.slice(0, 3).map(entity => {
            const quotationRef = entity?.notificationTemplate?.message?.includes('quotation no.')
              ? entity?.quotationId
              : entity?.bursaAPIBidCert?.ecertNo;
            const isRead = entity.dismissed;
            return (
              <Box
                key={entity.id}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: 1.5,
                  background: isBoubyanQuotationRoute
                    ? (isRead ? '#FAFAFA' : '#F0F0F0')
                    : (isRead ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.14)'),
                  border: isRead ? 'none' : `1px solid ${isBoubyanQuotationRoute ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.2)'}`,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      fontWeight={isRead ? 400 : 700}
                      fontSize={14}
                      lineHeight={1.3}
                      sx={{ color: isBoubyanQuotationRoute ? '#212121' : '#fff' }}
                    >
                      <Translate
                        contentKey={`global.rightMenuNotification.${entity.notificationTemplate.type}`}
                        interpolate={{ id: quotationRef }}
                      />
                    </Typography>
                    <Typography fontSize={12} sx={{ color: isBoubyanQuotationRoute ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.65)', mt: 0.4 }}>
                      {moment.utc(entity.createdDate).local().calendar()}
                    </Typography>
                  </Box>
                  <Typography
                    fontSize={11}
                    sx={{ color: isBoubyanQuotationRoute ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.55)', whiteSpace: 'nowrap', pt: 0.3 }}
                  >
                    {moment.utc(entity.createdDate).local().fromNow()}
                  </Typography>
                </Box>
                {entity.quotationId && (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{
                        textTransform: 'none',
                        color: isBoubyanQuotationRoute ? '#212121' : '#fff',
                        borderColor: isBoubyanQuotationRoute ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.4)',
                        fontSize: 12,
                        py: 0.25,
                        '&:hover': {
                          borderColor: isBoubyanQuotationRoute ? '#212121' : '#fff',
                          background: isBoubyanQuotationRoute ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                        },
                      }}
                      onClick={() => handleOpenOrderAndDismiss(entity.quotationId, entity.notificationTemplate.type, entity)}
                    >
                      <Translate contentKey="global.rightMenuNotification.openOrder" />
                    </Button>
                  </Box>
                )}
              </Box>
            );
          })}

          {allNotifications.length === 0 && (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Typography fontSize={13} sx={{ color: isBoubyanQuotationRoute ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.65)' }}>
                No notifications
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', py: 1.75 }}>
          <Button
            sx={{
              textTransform: 'none',
              color: isBoubyanQuotationRoute ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.85)',
              fontSize: 13,
              '&:hover': { color: isBoubyanQuotationRoute ? '#212121' : '#fff', background: 'transparent' },
            }}
            onClick={() => {
              setNotificationAnchorEl(null);
              setNotificationsDialogOpen(true);
            }}
          >
            <Translate contentKey="global.rightMenuNotification.viewAll" />
          </Button>
        </Box>
      </Popover>

      <NotificationsAllDialog
        open={notificationsDialogOpen}
        onClose={() => setNotificationsDialogOpen(false)}
        allNotifications={allNotifications}
        totalUnread={totalNotificationFunction()}
        onDismissAll={handleDismissedAll}
        onOpenOrder={handleOpenOrderAndDismiss}
      />

      {isAuthenticated && <EzraChatWidget />}
    </>
  );
};

export default HeaderNavBar;
